/**
 * Replay controls — wires the Record / Load&Play buttons in the top bar to
 * trace-viewer's ReplayRecorder / ReplayPlayer. Self-contained so `index.ts`
 * only needs one `setupReplayControls(viewer)` call on scene load.
 *
 * UI contract:
 *   - Click record: starts a recording against the current scene.
 *   - Click stop: stops, prompts the user to download a .replay.json.
 *   - Click load: opens a file picker; on a valid .replay.json, loads +
 *     starts playback. Click again to stop playback.
 *
 * Non-goals for v1:
 *   - No scrubbing, no marker editing, no record-session naming. The status
 *     line is a minimal "n events" / "t / duration" readout so the user can
 *     tell the state of the world without deeper UI.
 */

import { ReplayRecorder, ReplayPlayer, type ReplaySession } from 'trace-viewer/editor';
import { getUndoManager } from 'trace-viewer/editor';
import type { TraceViewerElement } from 'trace-viewer';

type ViewerProvider = () => TraceViewerElement | null | undefined;

type Mode = 'idle' | 'recording' | 'playing';

interface Controls {
    recordBtn: HTMLButtonElement;
    loadBtn: HTMLButtonElement;
    status: HTMLSpanElement;
    filePicker: HTMLInputElement;
}

export class ReplayControls {
    private controls: Controls;
    private getViewer: ViewerProvider;

    private mode: Mode = 'idle';
    private recorder: ReplayRecorder | null = null;
    private player: ReplayPlayer | null = null;
    private statusInterval: ReturnType<typeof setInterval> | null = null;

    constructor(getViewer: ViewerProvider) {
        this.getViewer = getViewer;
        const recordBtn = document.getElementById('replay-record') as HTMLButtonElement | null;
        const loadBtn = document.getElementById('replay-load') as HTMLButtonElement | null;
        const status = document.getElementById('replay-status');
        const filePicker = document.getElementById('replay-file-picker') as HTMLInputElement | null;
        if (!recordBtn || !loadBtn || !status || !filePicker) {
            throw new Error('[ReplayControls] Missing required DOM elements');
        }
        this.controls = { recordBtn, loadBtn, status, filePicker };
        this.wire();
    }

    private wire(): void {
        this.controls.recordBtn.addEventListener('click', () => this.onRecordClick());
        this.controls.loadBtn.addEventListener('click', () => this.onLoadClick());
        this.controls.filePicker.addEventListener('change', (e) => void this.onFileChosen(e));
    }

    // ---------- record ----------

    private onRecordClick(): void {
        if (this.mode === 'playing') {
            this.stopPlayback();
        }
        if (this.mode === 'recording') {
            this.stopRecordingAndSave();
            return;
        }
        this.startRecording();
    }

    private startRecording(): void {
        const traceScene = this.getViewer()?.traceScene;
        if (!traceScene) {
            console.warn('[ReplayControls] No scene loaded, cannot record');
            return;
        }
        this.recorder = new ReplayRecorder(traceScene, getUndoManager());
        this.recorder.start();
        this.setMode('recording');
        this.startStatusPolling();
    }

    private stopRecordingAndSave(): void {
        if (!this.recorder) return;
        const session = this.recorder.stop();
        this.recorder = null;
        this.setMode('idle');
        this.stopStatusPolling();
        downloadJson(session, `session-${new Date().toISOString().replace(/[:.]/g, '-')}.replay.json`);
        this.controls.status.textContent = `Saved ${session.events.length} events, ${(session.duration / 1000).toFixed(1)}s`;
    }

    // ---------- load + play ----------

    private onLoadClick(): void {
        if (this.mode === 'recording') {
            console.warn('[ReplayControls] Stop recording first');
            return;
        }
        if (this.mode === 'playing') {
            this.stopPlayback();
            return;
        }
        this.controls.filePicker.click();
    }

    private async onFileChosen(e: Event): Promise<void> {
        const input = e.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = ''; // allow reselecting the same file later
        if (!file) return;

        try {
            const text = await file.text();
            const session = JSON.parse(text) as ReplaySession;
            if (typeof session !== 'object' || session === null || !Array.isArray(session.events)) {
                throw new Error('Not a valid replay session');
            }
            await this.playSession(session);
        } catch (err) {
            console.error('[ReplayControls] Failed to load replay:', err);
            this.controls.status.textContent = `Error: ${(err as Error).message}`;
        }
    }

    private async playSession(session: ReplaySession): Promise<void> {
        const traceScene = this.getViewer()?.traceScene;
        if (!traceScene) {
            console.warn('[ReplayControls] No scene, cannot play');
            return;
        }
        this.player = new ReplayPlayer(traceScene, getUndoManager(), {
            onComplete: () => {
                this.controls.status.textContent = `Playback complete`;
                this.setMode('idle');
                this.stopStatusPolling();
                this.player = null;
            },
            onError: (err, event) => {
                console.error('[ReplayControls] Event error:', err, event);
            },
            onMarker: (note, t) => {
                console.log(`[ReplayControls] marker @${(t / 1000).toFixed(2)}s: ${note}`);
            },
        });
        await this.player.load(session);
        this.player.play();
        this.setMode('playing');
        this.startStatusPolling();
    }

    private stopPlayback(): void {
        this.player?.stop();
        this.player = null;
        this.setMode('idle');
        this.stopStatusPolling();
        this.controls.status.textContent = 'Stopped';
    }

    // ---------- status + mode ----------

    private setMode(mode: Mode): void {
        this.mode = mode;
        const rec = this.controls.recordBtn;
        const load = this.controls.loadBtn;

        rec.classList.toggle('active', mode === 'recording');
        load.classList.toggle('active', mode === 'playing');

        rec.title = mode === 'recording' ? 'Stop & save recording' : 'Record replay';
        load.title = mode === 'playing' ? 'Stop playback' : 'Load and play a .replay.json';
    }

    private startStatusPolling(): void {
        this.stopStatusPolling();
        this.statusInterval = setInterval(() => this.updateStatus(), 250);
        this.updateStatus();
    }

    private stopStatusPolling(): void {
        if (this.statusInterval !== null) {
            clearInterval(this.statusInterval);
            this.statusInterval = null;
        }
    }

    private updateStatus(): void {
        if (this.mode === 'recording' && this.recorder) {
            this.controls.status.textContent = `● Recording — ${this.recorder.eventCount} events`;
        } else if (this.mode === 'playing' && this.player) {
            // We don't know total duration without carrying the session ref,
            // but currentTime is sufficient as a lightweight readout.
            this.controls.status.textContent = `▶ ${(this.player.currentTime / 1000).toFixed(1)}s`;
        }
    }
}

function downloadJson(obj: unknown, filename: string): void {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Defer revoke: revoking synchronously after .click() races with the
    // browser's save dialog on slow machines / older Safari, producing
    // empty download files. One tick of delay is enough to let the browser
    // kick off the download before we invalidate the URL.
    setTimeout(() => URL.revokeObjectURL(url), 0);
}
